import Env from './Env';

const APP_START_TIME = new Date();
const ADJUSTMENT_STORAGE_KEY = 'timeTravelAdjustment';

// TIME_TRAVEL_ADJUSTMENT represents the number of milliseconds we will use to adjust any "new Date()"
// timestamps, as if we were travelling through time. We can use this feature to mock dates to see
// features that depend on the date, eg Mind the Gap content
let TIME_TRAVEL_ADJUSTMENT = Env.isDevEnv ? +sessionStorage.getItem(ADJUSTMENT_STORAGE_KEY) : 0;

export const setTimeTravelAdjustment = (newTimeTravelAdjustment = TIME_TRAVEL_ADJUSTMENT) => {
  TIME_TRAVEL_ADJUSTMENT = newTimeTravelAdjustment;
  if (Env.isDevEnv) {
    sessionStorage.setItem(ADJUSTMENT_STORAGE_KEY, TIME_TRAVEL_ADJUSTMENT);
  }
};

/**
 * Return the timestamp of when this user's session first started
 */
export const getAppStartTime = () => APP_START_TIME.getTime() + TIME_TRAVEL_ADJUSTMENT;

/**
 * This function allows us to mock the new time and date (on lower environments)
 * so we can QA test certain time-based features, like Mind The Gap content or seasonal features (eg, Halloween)
 * @param Date - newDateObj: the time being mocked as "now"
 */
export const setTimeTravelDate = (newDateObj) => {
  setTimeTravelAdjustment(newDateObj.getTime() - Date.now());
};

export const setTimeTravelFullDateStr = (newFullDateStr) => {
  const [yearStr, monthStr, dateStr] = newFullDateStr.split('-');
  const dateInMs = Date.now();
  const newDateInMs = new Date(dateInMs).setFullYear(yearStr, +monthStr - 1, dateStr);
  setTimeTravelAdjustment(newDateInMs - dateInMs);
};

/**
 * Return basic Date string in order to get info from timestamp ie, month, year, etc
 * Uses time travel adjustment if present in sessionStorage in lower envs (local, Sandbox, CI)
*/
export const getNewDate = (date) => {
  const baseDate = date ? new Date(date) : new Date();
  return new Date(baseDate.getTime() + TIME_TRAVEL_ADJUSTMENT);
};

/**
 * Return current timestamp in UTC ISO 8601 format, eg "2024-05-06T17:50:19.999Z"
 * Remove milliseconds part if not required, eg '2024-05-06T17:50:19.999Z' → '2024-05-06T17:50:19Z'
 * Dev Note: If a date is provided, it will already have TIME_TRAVEL_ADJUSTMENT applied from getNewDate()
 */
export const getTimestamp = (date, config = {}) => {
  const { isAddMilliseconds = false } = config;
  const baseDate = date ? new Date(date) : getNewDate();
  let dateStr = baseDate.toISOString();

  if (!isAddMilliseconds) {
    dateStr = `${dateStr.slice(0, 19)}Z`;
  }

  return dateStr;
};

export const getTimestampWithMs = date => getTimestamp(date, { isAddMilliseconds: true });

/**
 * Return current timestamp formatted in milliseconds
 * eg, current time is '2024-05-06T17:50:19.999Z', returns 1715017819999
 * Note: This should only be used to calculate elapsed time.
 * ** When sending data, use UTC format only. **
 */
export const getCurrentTimeInMs = () => getNewDate().getTime();

/**
 * Return current date
 * 0-indexed by default - eg, if in January, returns 0
 */
export const getCurrentMonth = () => getNewDate().getMonth();

/**
 * Return current year
 */
export const getCurrentYear = () => getNewDate().getFullYear();

/**
 * Returns the start of the current week in "YYYY-MM-DD" format, based on local device time.
 * Weeks start on Monday (ISO 8601). If an earliestDate is provided and falls after the
 * calculated Monday (e.g. the school year hasn't started yet), earliestDate is returned instead.
 * @param {string} [earliestDate] - Optional lower bound in "YYYY-MM-DD" format (e.g. school year start date)
 * @returns {string} e.g. "2026-03-30"
 */
export const getCurrentWeekStartDate = (earliestDate) => {
  const date = new Date();
  const day = date.getDay() || 7; // treat Sunday (0) as 7
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - (day - 1));

  const year = weekStart.getFullYear();
  const month = String(weekStart.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(weekStart.getDate()).padStart(2, '0');
  const weekStartDate = `${year}-${month}-${dayOfMonth}`;

  if (earliestDate && earliestDate > weekStartDate) {
    return earliestDate;
  }

  return weekStartDate;
};

/**
 * Converts seconds to minutes, rounded to the nearest minute with a minimum of 1.
 * Matches the TD app implementation - values that round to 0 are treated as 1 minute.
 * @param {number} seconds - Duration in seconds
 * @returns {number} Duration in whole minutes (minimum 1)
 */
export const secondsToMinutes = (seconds) => Math.max(1, Math.round(seconds / 60));
